import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { useRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { CheckIcon, ChevronDownIcon } from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { useLocale } from '../../../../../hooks/index';
import { StringKey } from '../../../../../lang/index';

const ALL_OPTION_VALUE = '__ALL__' as const;

export type MultiSelectFilterOption<T extends string> = {
  labelKey: StringKey;
  value: T;
};

type MultiSelectFilterProps<T extends string> = {
  allOptionLabelKey?: StringKey;
  disabled?: boolean;
  onChange: (value: T[]) => void;
  options: MultiSelectFilterOption<T>[];
  placeholderKey: StringKey;
  value: T[];
};

export const MultiSelectFilter = <T extends string>({
  allOptionLabelKey,
  disabled,
  onChange,
  options,
  placeholderKey,
  value,
}: MultiSelectFilterProps<T>) => {
  const { messagesLocale } = useLocale();
  const hadOnlyAllRef = useRef(value.length === 0 && !!allOptionLabelKey);

  hadOnlyAllRef.current = value.length === 0 && !!allOptionLabelKey;

  const listboxValue =
    value.length === 0 && allOptionLabelKey
      ? ([ALL_OPTION_VALUE] as (T | typeof ALL_OPTION_VALUE)[])
      : (value as (T | typeof ALL_OPTION_VALUE)[]);

  const allOptions = allOptionLabelKey
    ? [{ value: ALL_OPTION_VALUE, labelKey: allOptionLabelKey }, ...options]
    : options;

  const handleChange = (selected: (T | typeof ALL_OPTION_VALUE)[]) => {
    const withoutAll = selected.filter((v) => v !== ALL_OPTION_VALUE) as T[];
    if (selected.includes(ALL_OPTION_VALUE) && withoutAll.length > 0) {
      onChange(hadOnlyAllRef.current ? withoutAll : []);
    } else if (selected.includes(ALL_OPTION_VALUE)) {
      onChange([]);
    } else {
      onChange(withoutAll);
    }
  };

  const getDisplayLabel = (): React.ReactNode => {
    if (value.length === 0) {
      return <AppFormattedMessage id={placeholderKey} />;
    }
    const firstLabel = options.find((o) => o.value === value[0])?.labelKey;
    const firstText = firstLabel ? (messagesLocale[firstLabel] ?? firstLabel) : '';
    if (value.length === 1) {
      return firstText;
    }
    return (
      <>
        {firstText}
        <span className="text-gray-500"> +{value.length - 1}</span>
      </>
    );
  };

  return (
    <Listbox
      disabled={disabled}
      multiple
      onChange={(v) => handleChange(v as (T | typeof ALL_OPTION_VALUE)[])}
      value={listboxValue}
    >
      {({ open }) => (
        <div className={twMerge('relative', disabled && 'pointer-events-none')}>
          <ListboxButton
            className={twMerge(
              'flex h-9 min-w-[140px] cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-100 bg-gray-25 px-3 py-2 text-left text-sm font-[450] text-gray-700',
              disabled && 'cursor-default bg-gray-50 text-gray-400',
            )}
          >
            <span className="min-w-0 truncate">{getDisplayLabel()}</span>
            <ChevronDownIcon
              className={twMerge(
                'h-4 w-4 shrink-0 transition-transform duration-200',
                open && 'rotate-180',
              )}
              iconColor={disabled ? '#98a2b3' : '#344054'}
            />
          </ListboxButton>
          <ListboxOptions className="absolute right-0 z-20 mt-1 max-h-56 min-w-[200px] overflow-auto rounded-md border border-gray-200 bg-gray-25 p-1 shadow-lg">
            {allOptions.map((option) => (
              <ListboxOption
                className={({ focus, selected }) =>
                  twMerge(
                    'flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm text-gray-700',
                    focus && 'bg-gray-50',
                    selected && 'bg-brand-25 text-gray-700',
                  )
                }
                key={option.value}
                value={option.value}
              >
                {({ selected }) => (
                  <>
                    <span
                      className={twMerge(
                        'flex size-4 shrink-0 items-center justify-center rounded border',
                        selected ? 'border-brand-500 bg-brand-500' : 'border-gray-300 bg-white',
                      )}
                    >
                      {selected && <CheckIcon className="size-2.5" iconColor="#ffffff" />}
                    </span>
                    <AppFormattedMessage id={option.labelKey} />
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      )}
    </Listbox>
  );
};
